import React, {useCallback} from 'react';
import PropTypes from 'prop-types';
import {VelocityTransitionGroup} from 'velocity-react';
import {connect} from 'react-redux';
import {actions, select} from './connectors';
import {matchPath} from 'react-router-dom';
// constants
import {
  SHOPPING_CART_CONFIRM,
  SHOPPING_CART_CONFIRM_DIRTY
} from '~/constants/NotificationConstants';
import {SAVE, WELCOME, WELCOME_BACK} from '~/constants/WelcomeConstants';
import Animations from '~/constants/Animations';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
// components
import MainMenuBar from '~/components/MainMenuBar';
import Welcome_Lg from './Welcome_Lg';
import SaveCTA from './SaveCTA';
import {isEditDesignInCartMode} from '~/utils/url';

import withRouter, {routerPropTypes} from '~/router/withRouter';
import {useEffect, useState} from 'react';
import {useAuth} from '~/auth/AuthContext';
import {metrics} from '~/vendor/metrics';
import {getCheckoutSummaryLink} from '~/constants/CheckoutLink';
import {pushGtmEvent} from '../../utils/analytics';

const propTypes = {
  router: routerPropTypes.isRequired,
  analytics: PropTypes.shape({
    productColor: PropTypes.func,
    productWelcomeTag: PropTypes.func,
    textStart: PropTypes.func,
    uploadXPStart: PropTypes.func,
    welcome: PropTypes.func,
    welcomeBack: PropTypes.func,
    welcomeBackContinue: PropTypes.func,
    welcomeBackNew: PropTypes.func
  }),
  cartItemCount: PropTypes.number,
  clear: PropTypes.func,
  designName: PropTypes.string,
  email: PropTypes.string,
  fetchSavedDesigns: PropTypes.func,
  isCurrentDesignInCart: PropTypes.bool,
  isDesignDirty: PropTypes.bool,
  isFundraisingMode: PropTypes.bool,
  isMultiItemEligible: PropTypes.bool,
  notification: PropTypes.object,
  prompt: PropTypes.func,
  savedDesignsLength: PropTypes.number,
  welcome: PropTypes.string
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export function WelcomeContainer(props) {
  const {
    welcome: initialWelcome,
    router,
    cartItemCount,
    designName,
    isCurrentDesignInCart,
    isFundraisingMode,
    isRetrieveDesignsActive,
    retrieveDesignsValue,
    isMultiItemEligible,
    isDesignDirty,
    savedDesignsLength,
    analytics,
    clear,
    prompt,
    fetchSavedDesigns
  } = props;

  const auth = useAuth();

  useEffect(() => {
    pushGtmEvent({
      event: 'ab_test_group',
      ab_test_name: '2025 04 22 retrieve designs',
      ab_test_location: 'lab',
      ab_test_group: retrieveDesignsValue
    });
  }, []);

  const [state, setState] = useState({
    break: breakpoint(MediaQueries.largeAndAbove),
    welcome: initialWelcome
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MediaQueries.largeAndAbove);
    const mql = mq =>
      setState(prevState => ({...prevState, break: mq.matches}));

    mediaQuery.addListener(mql);

    return () => {
      mediaQuery.removeListener(mql);
    };
  }, []);

  const isStartFromDesignPage = useCallback(() => {
    return matchPath({path: '/startFromDesign'}, router.location.pathname);
  }, [router.location.pathname]);

  const newDesignAction = useCallback(
    e => {
      if (e) e.stopPropagation();
      clear();
      analytics.welcomeBackNew();
      router.navigate('/');
      if (state.break) {
        setState(prevState => ({...prevState, welcome: WELCOME}));
      }
    },
    [clear, analytics, router, state.break]
  );

  const startAction = useCallback(
    e => {
      e.stopPropagation();
      router.navigate('/');
    },
    [router]
  );

  const navigateWithCustomDimension = useCallback(
    (e, path, customDimension = '') => {
      e.stopPropagation();
      router.navigate(`${path}${customDimension}`);
    },
    [router]
  );

  const addArtAction = useCallback(
    (e, customDimension) => {
      navigateWithCustomDimension(e, '/addArt', customDimension);
    },
    [navigateWithCustomDimension]
  );

  const addTextAction = useCallback(
    (e, customDimension) => {
      navigateWithCustomDimension(e, '/addText', customDimension);
    },
    [navigateWithCustomDimension]
  );

  const changeProductColorAction = useCallback(
    (e, customDimension) => {
      analytics.productColor('welcome');
      navigateWithCustomDimension(e, '/productColor', customDimension);
    },
    [analytics, navigateWithCustomDimension]
  );

  const fireCustomDimension = useCallback(customDimension => {
    if (!customDimension) return;
    let location;
    switch (customDimension.split('=')[1]) {
      case 'wb':
        location = 'welcomeback';
        break;
      case 'rs':
        location = 'restingscreen';
        break;
      case 'w':
        location = 'welcome';
        break;
      default:
        location = 'toolbar';
    }
    metrics.setDimension('RestingScreenClick', location);
  }, []);

  const checkoutAction = useCallback(
    (e, customDimension) => {
      fireCustomDimension(customDimension);
      metrics.pageViewTag('ndx/reviewcart');
      if (isDesignDirty) {
        const key = isEditDesignInCartMode()
          ? SHOPPING_CART_CONFIRM
          : SHOPPING_CART_CONFIRM_DIRTY;
        prompt({key: key});
      } else {
        window.location.href = getCheckoutSummaryLink();
      }
    },
    [fireCustomDimension, isDesignDirty, prompt]
  );

  const productAction = useCallback(
    (e, customDimension) => {
      analytics.productWelcomeTag();
      navigateWithCustomDimension(e, '/productCatalog', customDimension);
    },
    [analytics, navigateWithCustomDimension]
  );

  const retrieveDesignsAction = useCallback(
    (e, customDimension) => {
      fireCustomDimension(customDimension);
      metrics.pageViewTag('ndx/retrieve/design');
      if (savedDesignsLength === 0) {
        fetchSavedDesigns({page: 1, email: auth.user?.email}).then(() => {
          navigateWithCustomDimension(e, '/savedDesigns', customDimension);
        });
      } else {
        navigateWithCustomDimension(e, '/savedDesigns', customDimension);
      }
    },
    [
      fireCustomDimension,
      savedDesignsLength,
      fetchSavedDesigns,
      auth.user?.email,
      navigateWithCustomDimension
    ]
  );

  const uploadAction = useCallback(
    (e, customDimension) => {
      navigateWithCustomDimension(e, '/uploadForm', customDimension);
      analytics.uploadXPStart({
        source: 'wb'
      });
    },
    [navigateWithCustomDimension, analytics]
  );

  const saveAction = useCallback(
    (e, customDimension) => {
      navigateWithCustomDimension(e, '/saveForm', customDimension);
    },
    [navigateWithCustomDimension]
  );

  const renderWelcomeBack = useCallback(() => {
    if (state.break) {
      return (
        <Welcome_Lg
          cartItemCount={cartItemCount}
          designName={designName}
          isCurrentDesignInCart={isCurrentDesignInCart}
          isEditFromCart={isEditDesignInCartMode()}
          isFundraisingMode={isFundraisingMode}
          isMultiItemEligible={isMultiItemEligible}
          isRetrieveDesignsActive={isRetrieveDesignsActive}
          isStartFromDesignPage={isStartFromDesignPage()}
          welcomeBack
          onAddArt={addArtAction}
          onAddText={addTextAction}
          onChangeProductColor={changeProductColorAction}
          onCheckout={checkoutAction}
          onNew={newDesignAction}
          onProduct={productAction}
          onRetrieveDesigns={retrieveDesignsAction}
          onTextStart={analytics.textStart}
          onStart={startAction}
          onUpload={uploadAction}
        />
      );
    } else {
      return <MainMenuBar router={router} />;
    }
  }, [
    state.break,
    cartItemCount,
    designName,
    isCurrentDesignInCart,
    isFundraisingMode,
    isMultiItemEligible,
    isStartFromDesignPage,
    addArtAction,
    addTextAction,
    changeProductColorAction,
    checkoutAction,
    newDesignAction,
    productAction,
    retrieveDesignsAction,
    analytics.textStart,
    startAction,
    uploadAction,
    router
  ]);

  const renderWelcome = useCallback(() => {
    if (state.break) {
      return (
        <Welcome_Lg
          cartItemCount={cartItemCount}
          designName={designName}
          isCurrentDesignInCart={isCurrentDesignInCart}
          isEditFromCart={isEditDesignInCartMode()}
          isFundraisingMode={isFundraisingMode}
          isMultiItemEligible={isMultiItemEligible}
          isRetrieveDesignsActive={isRetrieveDesignsActive}
          isStartFromDesignPage={isStartFromDesignPage()}
          onAddArt={addArtAction}
          onAddText={addTextAction}
          onChangeProductColor={changeProductColorAction}
          onCheckout={checkoutAction}
          onNew={newDesignAction}
          onProduct={productAction}
          onRetrieveDesigns={retrieveDesignsAction}
          onStart={startAction}
          onTextStart={analytics.textStart}
          onUpload={uploadAction}
        />
      );
    } else {
      return <MainMenuBar router={router} welcoming />;
    }
  }, [
    state.break,
    cartItemCount,
    designName,
    isCurrentDesignInCart,
    isFundraisingMode,
    isMultiItemEligible,
    isStartFromDesignPage,
    addArtAction,
    addTextAction,
    changeProductColorAction,
    checkoutAction,
    newDesignAction,
    productAction,
    retrieveDesignsAction,
    startAction,
    analytics.textStart,
    uploadAction,
    router
  ]);

  const renderSaveCTA = useCallback(
    customDimension => (
      <SaveCTA onFloatingLinkClick={e => saveAction(e, customDimension)} />
    ),
    [saveAction]
  );

  const renderContent = useCallback(() => {
    const customDimension = '?rs=rs';
    switch (state.welcome) {
      case WELCOME:
        return renderWelcome();
      case WELCOME_BACK:
        return renderWelcomeBack();
      case SAVE:
        return renderSaveCTA(customDimension);
      default:
        return renderWelcome();
    }
  }, [state.welcome, renderWelcome, renderWelcomeBack, renderSaveCTA]);

  const content = renderContent();
  return (
    !!content && (
      <div>
        <VelocityTransitionGroup leave={Animations.fadeOut}>
          {content}
        </VelocityTransitionGroup>
      </div>
    )
  );
}

WelcomeContainer.displayName = 'WelcomeContainer';
WelcomeContainer.propTypes = propTypes;

export default connect(
  state => select(state),
  dispatch => actions(dispatch)
)(withRouter(WelcomeContainer));
